/**
 * A casca do produto: a MOLDURA da v2 (spec 22), com as áreas do Hugi.
 *
 * ## De onde ela vem
 *
 * Desde 31/10 a casca é a `Moldura` do kit do `@cert4all/ui` — a mesma do Cert4All e do Stonen (C40 do
 * Cert4All). Cabeçalho com a marca e o contexto, módulos numa barra lateral (176 px, ou 48 recolhida) ou numa
 * faixa no topo, à escolha da pessoa, e o avatar com Ajuda e Sair. O componente é de lá; o que é do Hugi está
 * aqui: as áreas, as permissões, o tema e a cor (os tokens `--v2-*` apontam para o `vidro.css`, em `v2.css`).
 *
 * A API não mudou (`<Casca area rotuloDaArea acoes avisos areas>`): painel e atendimento a chamam como antes.
 *
 * ## Uma decisão que a arquitetura obriga
 *
 * Painel e inbox são **builds separados na mesma origem** (§3 da spec 02): `/` e `/atender`. Então a
 * navegação é **de documento** (`location.assign`), e não pelo router — atravessar de um bundle para o outro é
 * navegação de página, não troca de rota.
 *
 * ## A tela não rola
 *
 * O conteúdo vive em `.v2-conteudo`, que tem a altura da janela. A tela já migrada é uma `PaginaInteira` do kit
 * e não rola; a que ainda não migrou rola dentro de `.hugi-miolo` (a rede de segurança da trava
 * `scripts/trava-tela-nao-rola.mjs`, cuja lista de exceções só encolhe).
 */

import { useEffect, useState, type ReactNode } from "react";
import { Blocks, CalendarClock, CircleHelp, Contact, History, House, LifeBuoy, LogOut, Megaphone, SquareKanban, MessageCircle, MessagesSquare, Monitor, Moon, Radio, Send, Settings, Sparkles, Sun, TrendingUp, Users, Zap, type LucideIcon } from "lucide-react";
import { Moldura, useMenuDaMoldura, type AreaDaMoldura, type ItemDaConta } from "@cert4all/ui/kit";
import { sairDaSessao, useSessao } from "./sessao.js";
import { lerTema, salvarTema, seguirOSistema, type Tema } from "./tema.js";

/**
 * As seções da barra.
 *
 * A barra do Stonen agrupa por `.nav-section-label` — caixa alta, `letter-spacing: .15em`, peso 700 —
 * e o motivo é o mesmo aqui: onze itens numa lista contínua é uma parede.
 *
 * OS GRUPOS SÃO DE QUEM USA (spec 23, aprovado pelo owner em 01/11): até então "Atender" misturava o trabalho do dia
 * (Atendimento, Agenda, Chat) com vendas (Campanhas, Grupos, Funil) e configuração (Automações). Agora: o dia, a
 * venda, o que o Hugi faz sozinho, a análise e a configuração. O desenho da barra não muda; as permissões de cada
 * área também não.
 */
export type Secao = "hoje" | "vendas" | "automacao" | "analise" | "configuracoes";

export const SECOES: readonly { id: Secao; rotulo: string }[] = [
  { id: "hoje", rotulo: "Hoje" },
  { id: "vendas", rotulo: "Vendas" },
  { id: "automacao", rotulo: "Automação" },
  { id: "analise", rotulo: "Análise" },
  { id: "configuracoes", rotulo: "Configurações" },
];

export interface Area {
  readonly id: string;
  readonly secao: Secao;
  readonly rotulo: string;
  /** `null` quando a área não existe ainda — a barra a mostra pendente. */
  readonly href: string | null;
  readonly icone: LucideIcon;
  /** Ausente = sempre visível. */
  readonly permissao?: string;
  /** Aparece no `title` quando `href` é nulo. Deve dizer **o que falta**, não "em breve". */
  readonly porqueFalta?: string;
  /**
   * Sai do produto — abre em aba nova.
   *
   * Existe por causa da **Ajuda**, e o motivo é operacional: quem clica nela está atendendo alguém.
   * Navegar na mesma aba tira a pessoa da conversa para ler como fazer a coisa que ela ia fazer na
   * conversa. O rótulo diz que abre em outra aba, porque link que troca de janela sem avisar é o que
   * faz alguém clicar duas vezes.
   */
  readonly externa?: boolean;
}

const TAMANHO = 20;

/**
 * As áreas do produto.
 *
 * A ordem é a do §3 da spec 04, com `Ajuda` no fim — que é onde todo produto a põe, e mexer nisso
 * não compra nada.
 */
/*
 * A PERMISSÃO DE CADA ÁREA É A QUE O SERVIDOR EXIGE PARA MEXER NELA (2026-09-03).
 *
 * O owner entrou como Atendente e viu Agente Hugi, Fluxo de triagem e Equipe e acessos no menu — e o
 * Início trancado. Três áreas estavam com `read_conversation` (todo mundo) ou sem permissão nenhuma, e o
 * servidor exige `manage_integration` (agente), `manage_catalog` (fluxo, e o vínculo de gente
 * a fila na Equipe) e `read_channel` (o Início é a checklist de ativação, que lê os canais). Menu que
 * oferece o que a rota recusa é a família do "botão que não faz nada", com um cadeado no fim.
 * Supervisor continua vendo Equipe (ele distribui gente entre filas — H69) e não vê Agente.
 */
export const AREAS: readonly Area[] = [
  {
    // A tela inicial. Primeira na barra porque e' onde o checklist de ativacao vive (§4 da spec 04), e
    // e' o que um tenant novo precisa ver antes de qualquer outra coisa.
    id: "inicio",
    secao: "hoje",
    rotulo: "Início",
    href: "/",
    icone: House,
    permissao: "read_channel",
  },
  {
    id: "atender",
    secao: "hoje",
    rotulo: "Atendimento",
    href: "/atender",
    icone: MessagesSquare,
    permissao: "read_conversation",
  },
  {
    id: "contatos",
    secao: "vendas",
    rotulo: "Contatos",
    href: "/contatos",
    icone: Contact,
    // `read_conversation`, e nao `manage_contact`: a tela LE quem escreveu, e o recorte do §7.2 esta na
    // consulta. Exigir a permissao de gerenciar para ver a lista esconderia dela o atendente, que e'
    // quem mais procura contato.
    permissao: "read_conversation",
  },
  {
    id: "agenda",
    secao: "hoje",
    rotulo: "Agenda",
    href: "/agenda",
    icone: CalendarClock,
    permissao: "manage_schedule",
  },
  {
    /*
      Campanhas (spec 17): template para uma lista com opt-in. `read_report` abre a tela — ver as
      campanhas e' relatorio de entrega; disparar e' `send_campaign`, conferida DENTRO da tela,
      porque quem le' precisa ver o botao desabilitado com o motivo, e nao uma area que some.
    */
    id: "campanhas",
    secao: "vendas",
    rotulo: "Campanhas",
    href: "/campanhas",
    icone: Megaphone,
    permissao: "read_report",
  },
  {
    /*
      Publicações em grupos (spec 18): publicar em grupos de WhatsApp pela API Hugi, com agenda e
      resultado por grupo. `read_group` abre a tela — quem vê os grupos vê o módulo; criar, sincronizar
      e etiquetar são permissões próprias, conferidas dentro da tela (botão desabilitado com o
      motivo), pela mesma razão das campanhas.
    */
    id: "publicacoes",
    secao: "vendas",
    rotulo: "Grupos",
    href: "/publicacoes",
    icone: Send,
    permissao: "read_group",
  },
  {
    /*
      O funil de oportunidades (spec 10): quem responde a uma campanha vira um card aqui, sozinho.
      `read_conversation` abre a tela — todo mundo ve os proprios cards; ver os do time e os valores sao
      permissoes proprias (`funil:*`), recortadas na consulta, nao na barra.
    */
    id: "funil",
    secao: "vendas",
    rotulo: "Funil",
    href: "/funil",
    icone: SquareKanban,
    permissao: "read_conversation",
  },
  {
    /*
      AUTOMAÇÕES — uma área só, desde 05/09.

      Eram DUAS entradas, *Agente Hugi* e *Fluxos*, para a mesma coisa: *quando algo acontece, o Hugi
      faz*. O owner montou uma automação e se perdeu — *"não sei quando devo usar um ou o outro, nem a
      diferença real dos dois"* — e escolheu juntar. O assistente virou um recurso dentro da área.

      ⚠️ A PERMISSÃO É A MENOR DAS DUAS, e isso é escolha. O servidor exige `manage_catalog` para
      mexer na automação e `manage_integration` para mexer no assistente, e o Supervisor tem a
      primeira e não a segunda. Gatear a área pela maior esconderia dele a área inteira, inclusive as
      automações que ele administra; a segunda é conferida DENTRO da tela, com o motivo — o mesmo
      padrão de Campanhas e Publicações.
    */
    id: "automacoes",
    secao: "automacao",
    rotulo: "Automações",
    href: "/automacoes",
    icone: Zap,
    permissao: "manage_catalog",
  },
  {
    /*
      ASSISTENTES COM ENTRADA PRÓPRIA (spec 23, menu aprovado em 01/11): o assistente é o que responde sozinho — o
      gargalo do produto é ele não estar publicado —, e escondido na metade de baixo de Automações ninguém o achava.
      A tela é a mesma de Automações, aberta na lista de assistentes.
    */
    id: "assistentes",
    secao: "automacao",
    rotulo: "Assistentes",
    href: "/automacoes?mostrar=assistentes",
    icone: Sparkles,
    permissao: "manage_catalog",
  },
  {
    /*
      O chat da EQUIPE, e nao a conversa com o cliente.
      Fica em "atender" porque e' onde se usa — perguntar ao colega sobre um atendimento acontece durante
      o atendimento. O icone e' outro de proposito: dois icones de conversa iguais na mesma secao fariam
      alguem clicar no errado com o cliente esperando.
    */
    id: "chat",
    secao: "hoje",
    rotulo: "Chat da equipe",
    href: "/chat",
    icone: MessageCircle,
    permissao: "read_conversation",
  },
  {
    id: "relatorios",
    secao: "analise",
    rotulo: "Relatórios",
    href: "/relatorios",
    icone: TrendingUp,
    permissao: "read_report",
  },
  {
    id: "historico",
    secao: "analise",
    rotulo: "Histórico",
    // Era `/atender`, herdado de quando o inbox tinha a aba "Finalizados hoje" e nada mais. O historico
    // e' tela propria desde 2026-08-12: filtro por fila e motivo, tempos separados e exportacao.
    href: "/historico",
    icone: History,
    permissao: "read_conversation_history",
  },
  {
    id: "canais",
    secao: "configuracoes",
    rotulo: "Canais",
    href: "/canais",
    icone: Radio,
    permissao: "read_channel",
  },
  {
    id: "equipe",
    secao: "configuracoes",
    rotulo: "Equipe e acessos",
    href: "/equipe",
    icone: Users,
    // `read_conversation` para VER, e nao `manage_team`: um atendente precisa saber quem mais esta
    // atendendo e em que fila — e' isso que faz ele transferir para a pessoa certa. Mexer e' outra
    // permissao, conferida dentro da tela.
    permissao: "manage_catalog",
  },
  {
    id: "integracoes",
    secao: "configuracoes",
    rotulo: "Integrações e API",
    href: "/integracoes",
    icone: Blocks,
    permissao: "manage_integration",
  },
  {
    id: "ajustes",
    secao: "configuracoes",
    rotulo: "Ajustes",
    href: "/ajustes",
    icone: Settings,
    permissao: "manage_catalog",
  },
  {
    /*
      CHAMADOS (2026-09-11, ADR-0019): falar com o suporte da Cert4All — abrir, acompanhar, responder.
      O chamado mora LÁ; esta área é a janela dele. `open_support_ticket` é o mesmo portão das rotas
      `/api/painel/chamados`, e ele só esconde: quem recusa é a Cert4All (administrador da empresa ou
      grupo Tecnologia).

      ⚠️ Não é o "Acesso de suporte" do cabeçalho (S11) — aquele é o operador da plataforma abrindo o
      painel de um cliente. Por isso o nome é "Chamados", e não "Suporte".
    */
    id: "chamados",
    secao: "configuracoes",
    rotulo: "Chamados",
    href: "/chamados",
    icone: LifeBuoy,
    permissao: "open_support_ticket",
  },
  {
    /*
      Deixou de ser pendente em 2026-08-14: o centro de ajuda existe, com 29 páginas em `docs/ajuda/`.
      Ela é a ÚNICA área que mora fora do produto — é site estático, público e sem sessão, e por isso
      não tem `permissao`: quem está logado pode lê-la, e quem não está também.
    */
    id: "ajuda",
    secao: "configuracoes",
    rotulo: "Ajuda",
    href: "https://www.hugi.com.br/ajuda/",
    icone: CircleHelp,
    externa: true,
  },
];

/** O nome acessível da navegação, e o que a trava procura. */
const NOME_DA_NAVEGACAO = "Áreas do produto";

const OPCOES_DE_TEMA: Record<Tema, { readonly rotulo: string; readonly Icone: typeof Sun }> = {
  claro: { rotulo: "Claro", Icone: Sun },
  escuro: { rotulo: "Escuro", Icone: Moon },
  sistema: { rotulo: "Sistema", Icone: Monitor },
};

function proximoTema(atual: Tema): Tema {
  if (atual === "claro") return "escuro";
  if (atual === "escuro") return "sistema";
  return "claro";
}

/**
 * O tema, no cabeçalho: UM botão que cicla Claro → Escuro → Sistema. O título diz o atual E o próximo — sem o
 * próximo, a pessoa clica sem saber o que vai acontecer.
 */
export function SeletorDeTema() {
  const [tema, setTema] = useState<Tema>(() => lerTema());
  useEffect(() => seguirOSistema(() => setTema(lerTema())), []);

  const atual = OPCOES_DE_TEMA[tema];
  const proximo = OPCOES_DE_TEMA[proximoTema(tema)];
  return (
    <button
      type="button"
      className="v2-botao-icone"
      onClick={() => {
        const novo = proximoTema(tema);
        salvarTema(novo);
        setTema(novo);
      }}
      title={`Tema: ${atual.rotulo} — clique para ${proximo.rotulo.toLowerCase()}`}
      aria-label={`Tema: ${atual.rotulo}. Clique para ${proximo.rotulo.toLowerCase()}.`}
    >
      <atual.Icone size={16} aria-hidden="true" />
    </button>
  );
}

function irPara(href: string): void {
  window.location.assign(href);
}

/** A casca: a moldura com as áreas que a pessoa alcança. */
export function Casca(props: {
  readonly area: string;
  readonly rotuloDaArea: string;
  readonly children: ReactNode;
  /** No cabeçalho, antes do tema (o seletor de suporte, os avisos no aparelho). */
  readonly acoes?: ReactNode;
  /** Renderizado acima do conteúdo — banner de canal revogado (§7.5 da spec 02). */
  readonly avisos?: ReactNode;
  /**
   * Outro mapa de areas. Ausente = o do cliente (`AREAS`).
   *
   * Existe para o console, que tem navegacao propria. Sem isto ele mostrava "Atendimento" e "Contatos"
   * para quem opera a plataforma — links para telas que nao servem ao trabalho dele.
   */
  readonly areas?: readonly Area[] | undefined;
}) {
  const sessao = useSessao();
  // Lateral ou topo, e recolhida ou não: preferência da pessoa neste navegador (`hugi-menu:<usuário>`).
  const preferencias = useMenuDaMoldura("hugi", sessao.usuario.id);
  const [saindo, setSaindo] = useState(false);
  const [erroAoSair, setErroAoSair] = useState<string | null>(null);

  const todas = props.areas ?? AREAS;
  // A moldura não decide acesso: as áreas chegam filtradas. A Ajuda (externa) vai para o menu do avatar, e a área
  // pendente (sem `href`) não tem para onde levar.
  const areas: AreaDaMoldura[] = todas
    .filter((a) => !a.externa && a.href !== null)
    .filter((a) => !a.permissao || (sessao.produto.entitled && sessao.permissoes.includes(a.permissao)))
    .map((a) => ({
      id: a.id,
      rotulo: a.rotulo,
      modulo: a.secao,
      icone: a.icone,
      href: a.href!,
      ativa: a.id === props.area,
    }));

  async function sair() {
    if (saindo) return;
    setErroAoSair(null);
    setSaindo(true);
    const falha = await sairDaSessao();
    if (falha) setErroAoSair(falha);
    setSaindo(false);
  }

  const itensDaConta: ItemDaConta[] = [
    ...todas
      .filter((a) => a.externa && a.href)
      .map((a) => ({
        id: a.id,
        rotulo: `${a.rotulo} (abre em outra aba)`,
        icone: a.icone,
        acao: () => window.open(a.href!, "_blank", "noopener"),
      })),
    { id: "sair", rotulo: saindo ? "Saindo…" : "Sair", icone: LogOut, acao: () => void sair() },
  ];

  return (
    <Moldura
      marca={
        // A MARCA DE VERDADE (owner, 01/11: *"não tem a logo da Hugi"*): era um "H" num quadrado azul. É o
        // `favicon.svg` que os três builds servem na raiz (`web/ui/marca/publico/`, o master `icone.svg`).
        <span className="hugi-marca">
          <img className="hugi-marca-simbolo" src="/favicon.svg" width={30} height={30} alt="" aria-hidden="true" />
          <strong>Hugi</strong>
        </span>
      }
      nomeDaNavegacao={NOME_DA_NAVEGACAO}
      modulos={SECOES.map((s) => ({ id: s.id, nome: s.rotulo }))}
      areas={areas}
      contexto={sessao.tenant.nome ? { titulo: sessao.tenant.nome, detalhe: props.rotuloDaArea } : { titulo: props.rotuloDaArea }}
      extras={
        <>
          {props.acoes}
          {/* `entitled: false` não é erro, é caminho comercial (§5 regra 2): aviso permanente no cabeçalho. */}
          {sessao.produto.entitled ? null : <span className="hugi-aviso-de-plano">Hugi não habilitado</span>}
          <SeletorDeTema />
        </>
      }
      pessoa={{ nome: sessao.usuario.nome ?? sessao.usuario.email ?? `Usuário ${sessao.usuario.id}` }}
      itensDaConta={itensDaConta}
      aparencia="vidro"
      menu={preferencias.menu}
      aoMudarMenu={preferencias.mudarMenu}
      recolhido={preferencias.recolhido}
      aoRecolher={preferencias.mudarRecolhido}
      onNavegar={irPara}
    >
      {props.avisos}
      {/* Sair falhou: a tela diz, e NÃO finge que saiu. */}
      {erroAoSair ? (
        <p role="alert" className="hugi-erro-ao-sair">
          Não foi possível sair: {erroAoSair}
        </p>
      ) : null}
      <div className="hugi-miolo">{props.children}</div>
    </Moldura>
  );
}
